import React from 'react';
import styled, { css } from 'styled-components';
import { ClockCircleOutline } from 'antd-mobile-icons';
import type { PackTemplate } from '../../services/api/types';
import { walletService } from '../../services/api';
import { EnergyBatteryIcon } from '../icons';
import { r } from '../../theme/riderTheme';
import {
  PrimaryButton,
  GhostButton,
  ButtonChevron,
} from '../../theme/surfacePrimitives';
import { formatValidityHours } from '../../utils/packValidity';

interface PackRecommendationsProps {
  featuredPacks: PackTemplate[];
  currentWalletBalance: number;
  loading: boolean;
  onBuyPack: (pack: PackTemplate) => void;
  onViewAllPacks: () => void;
  onTopUpWallet: (amount: number) => void;
  scooterVin?: string;
}

type BadgeVariant = 'featured' | 'recommended' | 'popular';
type SpecKind = 'energy' | 'time';

const Container = styled.section`
  margin-bottom: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 0 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${({ theme }) => r(theme).text.primary};
`;

const SectionEyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 4px;
`;

const ViewAllLink = styled.button.attrs({ type: 'button' as const })`
  background: transparent;
  border: 0;
  padding: 4px 0;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.primary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 160ms ease;
  flex-shrink: 0;

  &::after {
    content: '→';
    font-family: 'Geist', sans-serif;
    font-size: 11px;
    letter-spacing: 0;
  }

  &:hover {
    color: ${({ theme }) => r(theme).accent.ok};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
    border-radius: 2px;
  }
`;

const PackGrid = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 4px 2px 8px;
  width: 100%;
  min-width: 0;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled.article<{ $featured?: boolean }>`
  position: relative;
  min-width: 264px;
  flex-shrink: 0;
  padding: 16px 16px 14px;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid
    ${({ theme, $featured }) =>
      $featured ? r(theme).text.primary : r(theme).surface.edge};
  border-radius: 14px;
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1), box-shadow 200ms ease;
  display: flex;
  flex-direction: column;

  ${({ $featured, theme }) =>
    $featured &&
    css`
      box-shadow: 0 0 0 3px ${r(theme).glow.selectionLime};

      &::after {
        content: '';
        position: absolute;
        top: 12px;
        right: 12px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${r(theme).accent.lime};
        box-shadow: 0 0 10px ${r(theme).accent.lime};
      }
    `}

  &:hover {
    transform: translateY(-1px);
  }
`;

const Header = styled.div`
  margin-bottom: 12px;
`;

const PackName = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 6px;
  overflow-wrap: anywhere;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const PriceCurrency = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const PriceAmount = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => r(theme).text.primary};
`;

const Badges = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const badgeColors = (theme: unknown, variant: BadgeVariant) => {
  const t = r(theme);
  switch (variant) {
    case 'featured':
      return {
        bg: t.surface.raisedSelected,
        border: t.accent.limeDeep,
        text: t.accent.ok,
      };
    case 'recommended':
      return {
        bg: 'rgba(0, 215, 255, 0.06)',
        border: 'rgba(0, 215, 255, 0.32)',
        text: '#0884a0',
      };
    case 'popular':
    default:
      return {
        bg: 'rgba(245, 158, 11, 0.06)',
        border: 'rgba(245, 158, 11, 0.32)',
        text: '#b45309',
      };
  }
};

const Badge = styled.span<{ $variant: BadgeVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ theme, $variant }) => badgeColors(theme, $variant).bg};
  border: 1px solid ${({ theme, $variant }) => badgeColors(theme, $variant).border};
  color: ${({ theme, $variant }) => badgeColors(theme, $variant).text};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const Specs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 14px;
`;

const Spec = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 10px;
  text-align: center;
`;

const SpecBadgeChar = styled.span<{ $kind: SpecKind }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 20px;
  border: 1px solid
    ${({ theme, $kind }) =>
      $kind === 'energy' ? r(theme).accent.limeDeep : r(theme).surface.edgeStrong};
  background: ${({ theme, $kind }) =>
    $kind === 'energy' ? r(theme).surface.raisedSelected : r(theme).surface.sheet};
  color: ${({ theme, $kind }) =>
    $kind === 'energy' ? r(theme).accent.ok : r(theme).text.muted};
  border-radius: 999px;
  line-height: 1;
  margin-bottom: 6px;

  & > svg {
    width: 13px;
    height: 13px;
    display: block;
  }
`;

const SpecValue = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: ${({ theme }) => r(theme).text.primary};
  line-height: 1.2;
`;

const SpecLabel = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-top: 3px;
`;

const TopUpNeeded = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #b45309;
  text-align: center;
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: center;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #f59e0b;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 36px 20px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px dashed ${({ theme }) => r(theme).surface.edge};
  border-radius: 12px;
`;

const EmptyTitle = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 6px;
`;

const EmptyBody = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => r(theme).text.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 28px 20px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};
`;

const PackRecommendations: React.FC<PackRecommendationsProps> = ({
  featuredPacks,
  currentWalletBalance,
  loading,
  onBuyPack,
  onViewAllPacks,
  onTopUpWallet,
  scooterVin,
}) => {
  const canAffordPack = (pack: PackTemplate): boolean =>
    currentWalletBalance >= pack.price_npr;

  const getTopUpAmount = (pack: PackTemplate): number =>
    Math.max(0, pack.price_npr - currentWalletBalance);

  const getPackBadges = (
    pack: PackTemplate,
  ): Array<{ variant: BadgeVariant; text: string }> => {
    const badges: Array<{ variant: BadgeVariant; text: string }> = [];
    if (pack.is_featured) badges.push({ variant: 'featured', text: 'Featured' });
    if (pack.recommended_for_scooter === scooterVin)
      badges.push({ variant: 'recommended', text: 'Recommended' });
    if (pack.price_npr <= 2000) badges.push({ variant: 'popular', text: 'Popular' });
    return badges;
  };

  const formatPackSpec = (
    pack: PackTemplate,
  ): Array<{ kind: SpecKind; value: string; label: string }> => {
    const specs: Array<{ kind: SpecKind; value: string; label: string }> = [];

    if (pack.ah_limit) {
      specs.push({
        kind: 'energy',
        value: `${pack.ah_limit} Ah`,
        label: 'Energy',
      });
    }

    const validityLabel = formatValidityHours(pack.validity_hours);
    if (validityLabel) {
      specs.push({
        kind: 'time',
        value: validityLabel,
        label: 'Validity',
      });
    }

    return specs;
  };

  const handleBuyPack = (pack: PackTemplate) => {
    if (canAffordPack(pack)) {
      onBuyPack(pack);
    } else {
      onTopUpWallet(getTopUpAmount(pack));
    }
  };

  if (loading) {
    return (
      <Container>
        <SectionHeader>
          <div>
            <SectionEyebrow>Pack · Recommended</SectionEyebrow>
            <SectionTitle>Recommended Packs</SectionTitle>
          </div>
        </SectionHeader>
        <LoadingState>Loading pack recommendations…</LoadingState>
      </Container>
    );
  }

  if (featuredPacks.length === 0) {
    return (
      <Container>
        <SectionHeader>
          <div>
            <SectionEyebrow>Pack · Recommended</SectionEyebrow>
            <SectionTitle>Energy Packs</SectionTitle>
          </div>
          <ViewAllLink onClick={onViewAllPacks}>Browse All</ViewAllLink>
        </SectionHeader>
        <EmptyState>
          <EmptyTitle>No packs available</EmptyTitle>
          <EmptyBody>Check back later for new energy pack options.</EmptyBody>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeader>
        <div>
          <SectionEyebrow>Pack · Recommended</SectionEyebrow>
          <SectionTitle>
            {scooterVin ? 'For your scooter' : 'Featured packs'}
          </SectionTitle>
        </div>
        <ViewAllLink onClick={onViewAllPacks}>View All</ViewAllLink>
      </SectionHeader>

      <PackGrid>
        {featuredPacks.map(pack => {
          const canAfford = canAffordPack(pack);
          const topUpNeeded = getTopUpAmount(pack);
          const badges = getPackBadges(pack);
          const specs = formatPackSpec(pack);

          return (
            <Card key={pack.id} $featured={pack.is_featured}>
              <Header>
                <PackName>{pack.name}</PackName>
                <PriceRow>
                  <PriceCurrency>NPR</PriceCurrency>
                  <PriceAmount>
                    {(pack.price_npr ?? 0).toLocaleString('en-IN')}
                  </PriceAmount>
                </PriceRow>
              </Header>

              {badges.length > 0 && (
                <Badges>
                  {badges.map((badge, index) => (
                    <Badge key={index} $variant={badge.variant}>
                      {badge.text}
                    </Badge>
                  ))}
                </Badges>
              )}

              {specs.length > 0 && (
                <Specs>
                  {specs.map((spec, index) => (
                    <Spec key={index}>
                      <SpecBadgeChar $kind={spec.kind} aria-hidden>
                        {spec.kind === 'energy' ? (
                          <EnergyBatteryIcon />
                        ) : (
                          <ClockCircleOutline />
                        )}
                      </SpecBadgeChar>
                      <SpecValue>{spec.value}</SpecValue>
                      <SpecLabel>{spec.label}</SpecLabel>
                    </Spec>
                  ))}
                </Specs>
              )}

              {canAfford ? (
                <PrimaryButton onClick={() => handleBuyPack(pack)}>
                  <span>Buy now</span>
                  <ButtonChevron>→</ButtonChevron>
                </PrimaryButton>
              ) : (
                <GhostButton onClick={() => handleBuyPack(pack)}>
                  <span>Top up &amp; buy</span>
                </GhostButton>
              )}

              {!canAfford && (
                <TopUpNeeded>
                  Need {walletService.formatCurrency(topUpNeeded)} more
                </TopUpNeeded>
              )}
            </Card>
          );
        })}
      </PackGrid>
    </Container>
  );
};

export default PackRecommendations;
